import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import { PrimaryButton } from "components/misc/Buttons.js";
import { createEncryptedCard } from "services/payment.services.js";
import { makePayment } from "adapters/payment.adapters.js";
import { FaCheckCircle, FaCheck, FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { SectionHeading } from "components/misc/Headings.js";
import { sensorConsulte } from "adapters/mts.adapters.js";

// Import images
import alertsImage from "assets/alerts_image.png";

const FormContainer = styled.div`
  ${tw`max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg`}
`;

const FormGroup = styled.div`
  ${tw`mb-6`}
`;

const Label = styled.label`
  ${tw`block text-sm font-medium text-gray-700 mb-2`}
`;

const Input = styled.input`
  ${tw`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
`;

const Row = styled.div`
  ${tw`flex gap-4`}
`;

const Column = styled.div`
  ${tw`flex-1`}
`;

const SubmitButton = styled(PrimaryButton)`
  ${tw`w-full mt-4`}
`;

const LoadingModal = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const LoadingContent = styled.div`
  ${tw`bg-white p-8 rounded-lg shadow-lg text-center max-w-sm w-full mx-4`}
`;

const Spinner = styled.div`
  ${tw`animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4`}
`;

const LoadingText = styled.p`
  ${tw`text-gray-700 text-lg font-medium`}
`;

const LoadingSubText = styled.p`
  ${tw`text-gray-500 text-sm mt-2`}
`;

const ProductBox = styled.div`
  ${tw`bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg mb-6 text-center`}
`;

const ProductTitle = styled.h3`
  ${tw`text-xl font-bold mb-2`}
`;

const ProductPrice = styled.p`
  ${tw`text-3xl font-bold`}
`;

const ProductDescription = styled.p`
  ${tw`text-blue-100 mt-2`}
`;

const PlanBox = styled.div`
  ${tw`bg-white p-6 rounded-lg shadow-lg mb-6 text-center border border-gray-300`}
`;

const PlanTitle = styled.h3`
  ${tw`text-lg font-bold mb-2`}
`;

const PlanDescription = styled.p`
  ${tw`text-gray-600 mb-4`}
`;

const PlanPrice = styled.p`
  ${tw`text-xl font-bold text-blue-600 mb-4`}
`;

const PlanButton = styled(PrimaryButton)`
  ${tw`w-full flex items-center justify-center gap-2`}
`;

const PlansContainer = styled.div`
  ${tw`flex flex-wrap gap-4 justify-center`}
`;

const PlanImage = styled.img`
  ${tw`w-16 h-16 mx-auto mb-4`}
`;

const BenefitsList = styled.ul`
  ${tw`mt-4 text-left`}
`;

const BenefitItem = styled.li`
  ${tw`flex items-center gap-2 text-gray-700 mb-2`}
`;

const SensorBox = styled.div`
  ${tw`bg-white p-6 rounded-lg shadow-lg mb-6 text-center border border-gray-300 max-w-md mx-auto`}
`;

const SensorImage = styled.img`
  ${tw`w-16 h-16 mx-auto mb-4`}
`;

const SensorTitle = styled.h3`
  ${tw`text-lg font-bold mb-4`}
`;

const SensorDescription = styled.p`
  ${tw`text-gray-600 mb-4`}
`;
const SensorDescriptionError = styled.p`
  ${tw`text-red-600 mb-4`}
`;

const SensorInput = styled.input`
  ${tw`w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4`}
`;

const SensorButton = styled(PrimaryButton)`
  ${tw`w-full flex items-center justify-center gap-2`}
`;

const BackButton = styled.button`
  ${tw`mb-4 px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2 transition-colors duration-200`}
`;

const SensorInfoBox = styled.div`
  ${tw`bg-white p-6 rounded-lg shadow-lg mb-6 text-center border border-gray-300 max-w-4xl mx-auto`}
`;
const SensorTextBox = styled.div`
  ${tw`mb-6 text-center max-w-4xl mx-auto`}
`;

const SensorInfoTitle = styled.h3`
  ${tw`text-lg font-bold`}
`;

const SensorInfoDescription = styled.p`
  ${tw`text-gray-600 mt-4`}
`;

const WaitingBox = styled.div`
  ${tw`bg-white p-6 rounded-lg shadow-lg mb-6 text-center border border-gray-300 max-w-md mx-auto`}
`;

const WaitingSpinner = styled.div`
  ${tw`animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4`}
`;

const WaitingTitle = styled.h3`
  ${tw`text-lg font-bold mb-4`}
`;

const WaitingDescription = styled.p`
  ${tw`text-gray-600`}
`;

const FormRow = styled.div`
  ${tw`flex flex-wrap gap-4`}
`;

const FormColumn = styled.div`
  ${tw`flex-1 min-w-[200px]`}
`;

const SuccessBox = styled.div`
  ${tw`bg-white p-8 rounded-lg shadow-lg mb-6 text-center border border-green-300 max-w-2xl mx-auto`}
`;

const SuccessIcon = styled.div`
  ${tw`w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4`}
`;

const SuccessTitle = styled.h2`
  ${tw`text-2xl font-bold text-green-700 mb-4`}
`;

const SuccessDescription = styled.p`
  ${tw`text-gray-600 mb-6 leading-relaxed`}
`;

const SuccessDetails = styled.div`
  ${tw`bg-gray-100 p-4 rounded-md mb-6 text-left`}
`;

const SuccessDetailItem = styled.div`
  ${tw`flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0`}
`;

const SuccessLabel = styled.span`
  ${tw`font-medium text-gray-700`}
`;

const SuccessValue = styled.span`
  ${tw`text-gray-900 font-semibold`}
`;

const NewPurchaseButton = styled(PrimaryButton)`
  ${tw`mt-4 mx-auto`}
`;

const ErrorBox = styled.div`
  ${tw`bg-white p-8 rounded-lg shadow-lg mb-6 text-center border border-red-300 max-w-2xl mx-auto`}
`;

const ErrorIcon = styled.div`
  ${tw`w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4`}
`;

const ErrorTitle = styled.h2`
  ${tw`text-2xl font-bold text-red-700 mb-4`}
`;

const ErrorDescription = styled.p`
  ${tw`text-gray-600 mb-6 leading-relaxed`}
`;

const RetryButton = styled(PrimaryButton)`
  ${tw`mt-4 mx-auto`}
`;

const Heading = tw(
    SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;

export default () => {
    const { uc } = useParams(); // Default values is 'comprar'
    const [formData, setFormData] = useState({
        nome: '',
        cpf: '',
        email: 'hcruzcongonhas@gmail.com',
        numeroCartao: '',
        mesVencimento: '',
        anoVencimento: '',
        cvc: ''
    });

    const plans = [
        { alertsNumbers: 30, price: 19.99, validity: '6 meses' },
        { alertsNumbers: 60, price: 37.99, validity: '6 meses' },
        { alertsNumbers: 120, price: 66.99, validity: '1 ano' },
        { alertsNumbers: 240, price: 119.99, validity: '1 ano' },
    ];

    const [sensorCode, setSensorCode] = useState((uc !== "comprar") ? uc : ''); // State for sensor code
    const [sensorCodeValid, setSensorCodeValid] = useState(false);
    const [currentAlertQuantityAvailable, setCurrentAlertQuantityAvailable] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [isConsulting, setIsConsulting] = useState(false); // State to manage consultation loading
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null); // State to store the selected plan
    const [title, setTitle] = useState("Compras de alertas para Sensores mTs"); // State to store the selected plan
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [paymentFinished, setPaymentFinished] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSensorCodeChange = (e) => {
        setSensorCode(e.target.value);
    };

    const handleSensorCodeSubmit = useCallback(async () => {
        if (!sensorCode.trim()) {
            setErrorMessage("Informe o código do Sensor.");
            return;
        }

        setIsConsulting(true); // Show loading box
        try {
            const response = await sensorConsulte(sensorCode);
            setCurrentAlertQuantityAvailable(response.data.alert_quantity_available);
            setErrorMessage('');
            setSensorCodeValid(true);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setErrorMessage("Sensor não encontrado. Verifique o código e tente novamente.");
                setSensorCodeValid(false);
            } else {
                setErrorMessage("Erro ao consultar o sensor. Tente novamente mais tarde.");
            }
        } finally {
            setIsConsulting(false); // Hide loading box
        }
    }, [sensorCode]);

    const handlePlanSelection = (planIndex) => {
        setSelectedPlan(planIndex);
        setTitle(`Pagamento de alertas para Sensor`);
        console.log("Selected Plan Index:", planIndex);
    };

    const handleGoBack = () => {
        setSelectedPlan(null);
        setTitle(`Compras de alertas para Sensores mTs`);
    };

    const handleNewPurchase = () => {
        setPaymentFinished(false);
        setPaymentSuccess(false);
        setSelectedPlan(null);
        setSensorCodeValid(false);
        setSensorCode('');
        setTitle(`Compras de alertas para Sensores mTs`);
        setFormData({
            nome: '',
            cpf: '',
            email: '',
            numeroCartao: '',
            mesVencimento: '',
            anoVencimento: '',
            cvc: ''
        });
    };

    const formatCPF = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const formatCardNumber = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})\d+?$/, '$1');
    };

    const handleCPFChange = (e) => {
        const formatted = formatCPF(e.target.value);
        setFormData(prev => ({
            ...prev,
            cpf: formatted
        }));
    };

    const handleCardNumberChange = (e) => {
        const formatted = formatCardNumber(e.target.value);
        setFormData(prev => ({
            ...prev,
            numeroCartao: formatted
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setLoadingMessage('Processando dados do cartão...');
        
        try {
            const encryptedCard = await createEncryptedCard(
                formData.nome,
                formData.numeroCartao.replace(/\s+/g, ''),
                formData.mesVencimento,
                formData.anoVencimento,
                formData.cvc
            );
            
            setLoadingMessage('Processando pagamento...');
            
            const response = await makePayment(
                encryptedCard.encryptedCard,
                formData.nome,
                formData.email,
                formData.cpf.replace(/\D/g, ''),
                (selectedPlan + 1).toString(),
                sensorCode
            );
            console.log(response);

            setIsLoading(false);
            setPaymentFinished(true);
            setPaymentSuccess(true);
            setTitle('Pagamento Realizado com Sucesso!');
            
        } catch (error) {
            setLoadingMessage('Erro no processamento');
            console.error('Payment error:', error);
            
            // Hide loading after error
            setTimeout(() => {
                setIsLoading(false);
                setPaymentFinished(true);
                setPaymentSuccess(false);
                setTitle('Erro no Pagamento');
            }, 3000);
        }
    };

    useEffect(() => {
        if (sensorCode.trim() === '')
            return;
        
        setIsConsulting(true); // Show loading box
        try {
            sensorConsulte(sensorCode).then((response) => {
                setCurrentAlertQuantityAvailable(response.data.alert_quantity_available);
                setErrorMessage('');
                setSensorCodeValid(true);
            }).catch((error) => {
                if (error.response && error.response.status === 404) {
                    setErrorMessage("Sensor não encontrado. Verifique o código e tente novamente.");
                    setSensorCodeValid(false);
                } else {
                    setErrorMessage("Erro ao consultar o sensor. Tente novamente mais tarde.");
                }
            });
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setErrorMessage("Sensor não encontrado. Verifique o código e tente novamente.");
                setSensorCodeValid(false);
            } else {
                setErrorMessage("Erro ao consultar o sensor. Tente novamente mais tarde.");
            }
        } finally {
            setIsConsulting(false); // Hide loading box
        }
    }, [sensorCode]);

    const paymentForm = () => (
        <div>
            <SensorTextBox>
                <SensorInfoDescription>
                    Você irá fazer o pagamento de compra de alertas para o Sensor {sensorCode} após a confirmação do pagamento, os alertas são adicionados instantaneamente. Qualquer dúvida estamos à disposição, obrigado.
                    <br /><br />
                    Preencha o formulário abaixo com seus dados e informações do cartão de crédito para concluir a compra com segurança.
                </SensorInfoDescription>
            </SensorTextBox>
            <FormContainer>
                <BackButton onClick={handleGoBack}>
                    <FaArrowLeft /> Escolher outro pacote de alertas
                </BackButton>
                <ProductBox>
                    <ProductTitle>Compra de pacote de alertas para Sensores</ProductTitle>
                    <ProductPrice>R$ {plans[selectedPlan].price.toFixed(2).replace('.', ',')}</ProductPrice>
                    <ProductDescription>{plans[selectedPlan].alertsNumbers} alertas para WhatsApp e E-mail</ProductDescription>
                </ProductBox>
                
                <form onSubmit={handleSubmit}>
                    <FormRow>
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="nome">Nome Completo</Label>
                                <Input
                                    type="text"
                                    id="nome"
                                    name="nome"
                                    value={formData.nome}
                                    onChange={handleInputChange}
                                    placeholder="Digite seu nome completo"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="cpf">CPF</Label>
                                <Input
                                    type="text"
                                    id="cpf"
                                    name="cpf"
                                    value={formData.cpf}
                                    onChange={handleCPFChange}
                                    placeholder="000.000.000-00"
                                    maxLength="14"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                    </FormRow>

                    <FormRow>
                        {/*
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="email">E-mail</Label>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="seu@email.com"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                        */}
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="numeroCartao">Número do Cartão</Label>
                                <Input
                                    type="text"
                                    id="numeroCartao"
                                    name="numeroCartao"
                                    value={formData.numeroCartao}
                                    onChange={handleCardNumberChange}
                                    placeholder="0000 0000 0000 0000"
                                    maxLength="19"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                    </FormRow>

                    <FormRow>
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="mesVencimento">Mês</Label>
                                <Input
                                    type="number"
                                    id="mesVencimento"
                                    name="mesVencimento"
                                    value={formData.mesVencimento}
                                    onChange={handleInputChange}
                                    placeholder="MM"
                                    min="1"
                                    max="12"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="anoVencimento">Ano</Label>
                                <Input
                                    type="number"
                                    id="anoVencimento"
                                    name="anoVencimento"
                                    value={formData.anoVencimento}
                                    onChange={handleInputChange}
                                    placeholder="AAAA"
                                    min="2024"
                                    max="2040"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                        <FormColumn>
                            <FormGroup>
                                <Label htmlFor="cvc">CVC</Label>
                                <Input
                                    type="text"
                                    id="cvc"
                                    name="cvc"
                                    value={formData.cvc}
                                    onChange={handleInputChange}
                                    placeholder="123"
                                    maxLength="4"
                                    pattern="[0-9]{3,4}"
                                    required
                                    disabled={isLoading} // Disable input when loading
                                />
                            </FormGroup>
                        </FormColumn>
                    </FormRow>

                    <SubmitButton type="submit" disabled={isLoading}>
                        {isLoading && <Spinner tw="h-5 w-5 inline-block mr-2" />}
                        {isLoading ? 'Processando...' : 'Finalizar Compra'}
                    </SubmitButton>
                </form>
            </FormContainer>
        </div>
    );

    return (
        <>
            <PlansContainer>
                <div style={{ width: '80%', marginBottom: '40px', marginTop: '20px', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                    <Heading>{title}</Heading>
                </div>
            </PlansContainer>
            {(!sensorCodeValid) ?
                <div>
                    <PlansContainer>
                        {!isConsulting ? (
                            <SensorBox>
                                <SensorTitle>Para qual Sensor você<br />deseja colocar crédito</SensorTitle>
                                <SensorDescription>
                                    Digite o código único do sensor que você deseja colocar créditos de mensagens. Esse código você pode obter no Aplicativo. Qualquer dúvida, entre em contato com nosso suporte.
                                </SensorDescription>
                                <SensorDescriptionError>
                                    {errorMessage}
                                </SensorDescriptionError>
                                <SensorInput
                                    type="text"
                                    placeholder="Digite o código do sensor"
                                    value={sensorCode}
                                    onChange={handleSensorCodeChange}
                                />
                                <SensorButton onClick={handleSensorCodeSubmit}>
                                    <FaArrowRight /> Escolher pacotes
                                </SensorButton>
                            </SensorBox>
                        ) : (
                            <WaitingBox>
                                <div style={{ paddingTop: '20px', paddingBottom: '20px' }}>
                                    <WaitingSpinner />
                                </div>
                                <WaitingTitle>Consultando Sensor</WaitingTitle>
                                <WaitingDescription>
                                    Estamos buscando informações do seu Sensor mTs.<br /><br />Por favor, aguarde alguns instantes.
                                </WaitingDescription>
                            </WaitingBox>
                        )}
                    </PlansContainer>
                </div>
            :(selectedPlan !== null && sensorCodeValid) && !paymentFinished ?
                paymentForm()
            :(paymentFinished) ?
                (paymentSuccess) ?
                    <div>
                        <SuccessBox>
                            <SuccessIcon>
                                <FaCheckCircle tw="text-white text-2xl" />
                            </SuccessIcon>
                            <SuccessTitle>Pagamento Realizado com Sucesso!</SuccessTitle>
                            <SuccessDescription>
                                Seu pagamento foi processado com sucesso e os alertas foram adicionados ao seu sensor imediatamente.
                            </SuccessDescription>
                            
                            <SuccessDetails>
                                <SuccessDetailItem>
                                    <SuccessLabel>Sensor:</SuccessLabel>
                                    <SuccessValue>{sensorCode}</SuccessValue>
                                </SuccessDetailItem>
                                <SuccessDetailItem>
                                    <SuccessLabel>Pacote:</SuccessLabel>
                                    <SuccessValue>{plans[selectedPlan]?.alertsNumbers} alertas</SuccessValue>
                                </SuccessDetailItem>
                                <SuccessDetailItem>
                                    <SuccessLabel>Valor Pago:</SuccessLabel>
                                    <SuccessValue>R$ {plans[selectedPlan]?.price.toFixed(2).replace('.', ',')}</SuccessValue>
                                </SuccessDetailItem>
                                <SuccessDetailItem>
                                    <SuccessLabel>Validade:</SuccessLabel>
                                    <SuccessValue>{plans[selectedPlan]?.validity}</SuccessValue>
                                </SuccessDetailItem>
                            </SuccessDetails>
                            
                            <SuccessDescription>
                                Os alertas já estão ativos em seu sensor. Se tiver alguma dúvida, entre em contato com nosso suporte.
                            </SuccessDescription>
                            
                            <NewPurchaseButton onClick={handleNewPurchase}>
                                Fazer Nova Compra
                            </NewPurchaseButton>
                        </SuccessBox>
                    </div>
                :
                    <div>
                        <ErrorBox>
                            <ErrorIcon>
                                <FaCheckCircle tw="text-white text-2xl" />
                            </ErrorIcon>
                            <ErrorTitle>Erro no Pagamento</ErrorTitle>
                            <ErrorDescription>
                                Ocorreu um problema ao processar o pagamento. Por favor, verifique os dados do cartão e tente novamente.
                            </ErrorDescription>
                            <RetryButton onClick={handleNewPurchase}>
                                Tentar Novamente
                            </RetryButton>
                        </ErrorBox>
                    </div>
            :
                <div>
                    <SensorInfoBox>
                        <SensorInfoTitle>
                            Adicionar créditos de alertas WhatsApp e E-mail para o Sensor {sensorCode}
                        </SensorInfoTitle>
                        <SensorInfoDescription>
                            Escolha o pacote que deseja abaixo. O crédito será adicionado ao Sensor {sensorCode} após a confirmação do pagamento e você passa a receber os alertas em instântes. Qualquer dúvida estamos à disposição, obrigado.
                            <br /><br />
                            Atualmente o Sensor {sensorCode} possui {currentAlertQuantityAvailable} alertas disponíveis. O crédito adquirido será somado a esse valor.
                        </SensorInfoDescription>
                    </SensorInfoBox>
                    <div>
                        <PlansContainer>
                            {plans.map((plan, index) => (
                                <PlanBox key={index}>
                                    <PlanTitle>{plan.alertsNumbers} alertas</PlanTitle>
                                    <PlanDescription>{plan.alertsNumbers} alertas para WhatsApp e E-mail</PlanDescription>
                                    <PlanPrice>R$ {plan.price.toFixed(2).replace('.', ',')}</PlanPrice>
                                    <BenefitsList>
                                        <BenefitItem>
                                            <FaCheck tw="text-blue-500" /> {plan.alertsNumbers} Alerts
                                        </BenefitItem>
                                        <BenefitItem>
                                            <FaCheck tw="text-blue-500" /> WhatsApp e E-mail
                                        </BenefitItem>
                                        <BenefitItem>
                                            <FaCheck tw="text-blue-500" /> Válidos por {plan.validity}
                                        </BenefitItem>
                                        <BenefitItem>
                                            <FaCheck tw="text-blue-500" /> Ativação imediata
                                        </BenefitItem>
                                    </BenefitsList>
                                    <PlanButton onClick={() => handlePlanSelection(index)}>
                                        <FaCheckCircle /> Escolher
                                    </PlanButton>
                                </PlanBox>
                            ))}
                        </PlansContainer>
                    </div>
                </div>
            }
        </>
    );
}
